import { useState, useEffect, useRef } from "react";
import { X, Loader2, ShieldCheck } from "lucide-react";
import { CONFIG } from "@/constants/testIds";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LeadDialog — modulo di contatto.
 *
 * Props:
 *   open          bool        mostra/nasconde il dialog
 *   onClose       fn()        callback di chiusura
 *   tipo          "privato"|"azienda"
 *   snapshot      object      dati dal configuratore (opzionale)
 *   guideSlug     string      se presente, mostra il form in modalità guida
 *   slim          bool        modalità snella (solo configuratore) – retrocompatibile
 *   prefillCitta  string      città da pre-compilare (da indirizzo step 1)
 */
export default function LeadDialog({
  open,
  onClose,
  tipo,
  snapshot,
  guideSlug,
  slim = false,
  prefillCitta = "",
}) {
  // Stato form
  const [form, setForm] = useState({
    nome: "", cognome: "", ragione_sociale: "",
    email: "", telefono: "", citta: "", provincia: "",
    messaggio: "",
    canale_preferito: "qualsiasi",
    consenso_privacy: false, consenso_marketing: false, consenso_partner: false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Idempotency key: generata all'apertura, mantenuta in retry, azzerata alla chiusura.
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Genera UUID solo alla prima apertura; i retry usano lo stesso UUID.
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current =
          typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }
      setForm({
        nome: "", cognome: "", ragione_sociale: "",
        email: "", telefono: "", citta: slim ? prefillCitta : "", provincia: "",
        messaggio: slim ? "" : snapshot?.messaggio || "",
        canale_preferito: "qualsiasi",
        consenso_privacy: false, consenso_marketing: false, consenso_partner: false,
      });
    } else {
      // Azzera la chiave alla chiusura (prossima apertura = nuova sessione)
      idempotencyKeyRef.current = null;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const upd = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  // Nel modulo slim: il telefono è richiesto solo se il canale è telefono o whatsapp.
  const telefonoRequired = slim && (form.canale_preferito === "telefono" || form.canale_preferito === "whatsapp");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consenso_privacy) {
      toast.error("Devi confermare di aver letto l'informativa privacy");
      return;
    }
    if (slim && telefonoRequired && !form.telefono.trim()) {
      toast.error("Inserisci il numero di telefono per il canale scelto");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        tipo,
        ...snapshot,
        nome: form.nome.trim(),
        cognome: form.cognome.trim() || undefined,
        ragione_sociale: tipo === "azienda" ? form.ragione_sociale.trim() : undefined,
        email: form.email.trim(),
        telefono: form.telefono.trim() || undefined,
        citta: form.citta.trim(),
        provincia: form.provincia.trim() || undefined,
        messaggio: form.messaggio.trim() || undefined,
        fonte: guideSlug ? `guida-${guideSlug}` : "configuratore",
        canale_preferito: slim ? form.canale_preferito : undefined,
        consenso_privacy: form.consenso_privacy,
        consenso_marketing: form.consenso_marketing,
        consenso_partner: form.consenso_partner,
        // Idempotency: stesso UUID per retry identici nella stessa sessione
        idempotency_key: idempotencyKeyRef.current,
      };

      await axios.post(`${API}/leads`, payload);

      if (guideSlug) {
        toast.success("Grazie! Ora scaricheremo la tua guida PDF.");
        const a = document.createElement("a");
        a.href = `${API}/guides/${guideSlug}`;
        a.target = "_blank";
        a.rel = "noreferrer";
        a.download = `guida-${guideSlug}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        toast.success("Richiesta inviata! Di solito rispondiamo entro 24 ore.");
      }
      onClose();
    } catch (err) {
      // 409 = stessa chiave, dati diversi (rarissimo con UUID v4) oppure concorrenza
      if (err?.response?.status === 409) {
        toast.error(
          err?.response?.data?.detail ||
          "Richiesta già ricevuta con dati diversi. Ricarica la pagina e riprova."
        );
        return; // Non resettare: l'utente riprova con gli stessi dati
      }
      const msg = err?.response?.data?.detail || "Errore invio richiesta. Riprova.";
      toast.error(msg);
      // Dati e UUID conservati: il retry riusa lo stesso UUID (idempotente)
    } finally {
      setSubmitting(false);
    }
  };

  const isGuide = !!guideSlug;
  const overline = isGuide ? "Guida PDF gratuita" : "Consulenza gratuita";
  const title = isGuide
    ? (tipo === "azienda" ? "Guida aziende e capannoni" : "Guida fotovoltaico per la casa")
    : (tipo === "azienda" ? "Verifichiamo la stima sulla tua azienda" : "Verifichiamo la stima sul tuo caso");
  const submitLabel = isGuide ? "Scarica la guida" : "Invia richiesta";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid={CONFIG.leadForm}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#0a1f1a] border border-white/15 text-white max-h-[92vh] overflow-y-auto rounded-md"
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between bg-[#0a1f1a] border-b border-white/10 px-6 py-4">
          <div>
            <div className="overline text-emerald-400">{overline}</div>
            <div className="font-display font-extrabold text-xl tracking-tight text-white">{title}</div>
          </div>
          <button type="button" aria-label="Chiudi modulo" onClick={onClose} className="p-2 hover:bg-white/5 rounded-sm">
            <X size={18} />
          </button>
        </div>

        {isGuide && (
          <div className="px-6 pt-4">
            <p className="text-sm text-white/80 leading-relaxed">
              {tipo === "azienda"
                ? "19 pagine illustrate su consumi dell'impresa, qualità della posa, coperture, documenti e regole da verificare per il capannone."
                : "19 pagine illustrate su componenti, posa sul tetto, sicurezza, documenti e sei domande da fare all'installatore."
              }{" "}
              Compila il modulo per scaricare il PDF. L&apos;analisi del preventivo è facoltativa; marketing e contatto con partner non sono necessari per ottenere la guida.
            </p>
          </div>
        )}

        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">

          {/* ── DATI ANAGRAFICI ── */}
          {slim ? (
            /* Modalità snella (configuratore) */
            <>
              <Field label="Nome *">
                <input
                  required
                  value={form.nome}
                  onChange={upd("nome")}
                  className="input"
                  data-testid={CONFIG.leadNome}
                  autoComplete="given-name"
                />
              </Field>
              <Field label="Cognome">
                <input
                  value={form.cognome}
                  onChange={upd("cognome")}
                  className="input"
                  data-testid={CONFIG.leadCognome}
                  placeholder="Opzionale"
                  autoComplete="family-name"
                />
              </Field>
              {tipo === "azienda" && (
                <Field label="Ragione sociale *" wide>
                  <input
                    required
                    value={form.ragione_sociale}
                    onChange={upd("ragione_sociale")}
                    className="input"
                    data-testid={CONFIG.leadRagioneSociale}
                  />
                </Field>
              )}
            </>
          ) : (
            /* Modalità standard (guide, analisi-preventivo) */
            tipo === "privato" ? (
              <>
                <Field label="Nome *">
                  <input required value={form.nome} onChange={upd("nome")} className="input" data-testid={CONFIG.leadNome} />
                </Field>
                <Field label="Cognome *">
                  <input required value={form.cognome} onChange={upd("cognome")} className="input" data-testid={CONFIG.leadCognome} />
                </Field>
              </>
            ) : (
              <>
                <Field label="Ragione sociale *" wide>
                  <input required value={form.ragione_sociale} onChange={upd("ragione_sociale")} className="input" data-testid={CONFIG.leadRagioneSociale} />
                </Field>
                <Field label="Referente *">
                  <input required value={form.nome} onChange={upd("nome")} className="input" data-testid={CONFIG.leadNome} />
                </Field>
                <Field label="Cognome referente">
                  <input value={form.cognome} onChange={upd("cognome")} className="input" data-testid={CONFIG.leadCognome} />
                </Field>
              </>
            )
          )}

          {/* Email sempre obbligatoria */}
          <Field label="Email *">
            <input
              type="email"
              required
              value={form.email}
              onChange={upd("email")}
              className="input"
              data-testid={CONFIG.leadEmail}
              autoComplete="email"
            />
          </Field>

          {/* ── CANALE + TELEFONO (solo slim) ── */}
          {slim ? (
            <>
              <Field label="Come preferisci essere contattato?">
                <select
                  value={form.canale_preferito}
                  onChange={upd("canale_preferito")}
                  className="input"
                  data-testid="lead-canale-preferito"
                >
                  <option value="qualsiasi">Qualsiasi</option>
                  <option value="email">Solo email</option>
                  <option value="telefono">Telefono</option>
                  <option value="whatsapp">WhatsApp</option>
                </select>
              </Field>
              {telefonoRequired && (
                <Field label={`Telefono${telefonoRequired ? " *" : ""}`}>
                  <input
                    type="tel"
                    required={telefonoRequired}
                    autoComplete="tel"
                    value={form.telefono}
                    onChange={upd("telefono")}
                    className="input"
                    data-testid={CONFIG.leadTelefono}
                  />
                </Field>
              )}
              <Field label={`Città${prefillCitta ? " (precompilata)" : ""}`}>
                <input
                  value={form.citta}
                  onChange={upd("citta")}
                  className="input"
                  data-testid={CONFIG.leadCitta}
                  placeholder="Comune"
                />
              </Field>
            </>
          ) : (
            /* Modalità standard: telefono + città + provincia sempre visibili */
            <>
              <Field label="Telefono *">
                <input
                  type="tel"
                  required
                  autoComplete="tel"
                  value={form.telefono}
                  onChange={upd("telefono")}
                  className="input"
                  data-testid={CONFIG.leadTelefono}
                />
              </Field>
              <Field label="Città *">
                <input
                  required
                  value={form.citta}
                  onChange={upd("citta")}
                  className="input"
                  data-testid={CONFIG.leadCitta}
                />
              </Field>
              <Field label="Provincia">
                <input
                  value={form.provincia}
                  onChange={upd("provincia")}
                  maxLength={2}
                  placeholder="MI"
                  className="input"
                  data-testid={CONFIG.leadProvincia}
                />
              </Field>
              <Field label="Messaggio (opzionale)" wide>
                <textarea
                  rows={3}
                  value={form.messaggio}
                  onChange={upd("messaggio")}
                  className="input resize-none"
                  data-testid={CONFIG.leadMessaggio}
                />
              </Field>
            </>
          )}

          {/* ── CONSENSI ── */}
          <div className="md:col-span-2 border-t border-white/10 pt-4 space-y-3">
            <div className="flex items-start gap-2 text-xs text-white/60">
              <ShieldCheck size={14} className="text-emerald-400 mt-0.5 shrink-0" />
              <span>
                I tuoi dati sono trattati secondo il Reg. UE 2016/679 (GDPR).
                Leggi l&apos;<Link to="/privacy" className="underline text-emerald-400">informativa completa</Link>.
                {" "}Il rifiuto dei consensi facoltativi non impedisce l&apos;analisi gratuita.
              </span>
            </div>

            <label className="flex items-start gap-3 text-xs cursor-pointer text-white/80">
              <input
                type="checkbox"
                required
                checked={form.consenso_privacy}
                onChange={upd("consenso_privacy")}
                data-testid={CONFIG.leadPrivacy}
                className="mt-0.5 accent-emerald-500"
                aria-label="Presa visione informativa privacy (obbligatoria)"
              />
              <span>
                Ho letto e compreso l&apos;<Link to="/privacy" className="underline text-emerald-400">informativa privacy</Link>.
                Autorizzo il trattamento dei dati per gestire la mia richiesta e svolgere l&apos;analisi gratuita del preventivo.
                {" "}<span className="text-red-400">*</span>
              </span>
            </label>

            <label className="flex items-start gap-3 text-xs cursor-pointer text-white/80">
              <input
                type="checkbox"
                checked={form.consenso_marketing}
                onChange={upd("consenso_marketing")}
                data-testid={CONFIG.leadMarketing}
                className="mt-0.5 accent-emerald-500"
                aria-label="Consenso marketing (facoltativo)"
              />
              <span>Acconsento a ricevere aggiornamenti e comunicazioni promozionali di Bandofotovoltaico24 (facoltativo).</span>
            </label>

            <label className="flex items-start gap-3 text-xs cursor-pointer text-white/80">
              <input
                type="checkbox"
                checked={form.consenso_partner}
                onChange={upd("consenso_partner")}
                data-testid="lead-partner"
                className="mt-0.5 accent-emerald-500"
                aria-label="Consenso comunicazione a installatori partner (facoltativo)"
              />
              <span>
                Acconsento a essere messo in contatto con installatori e professionisti selezionati da Bandofotovoltaico24,
                che potranno utilizzare i miei recapiti per fornirmi informazioni o proposte (facoltativo).
              </span>
            </label>
          </div>

          {/* ── ACTIONS ── */}
          <div className="md:col-span-2 flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm px-4 py-2.5 text-white/70 hover:text-white"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid={CONFIG.leadSubmit}
              className="btn-primary text-sm"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
              {submitting ? "Invio in corso…" : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <label className={`flex flex-col gap-1.5 ${wide ? "md:col-span-2" : ""}`}>
      <span className="text-xs uppercase tracking-wider text-white/60">{label}</span>
      {children}
    </label>
  );
}
